/**
 * All study CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.study;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_STUDY = "INVALIDATE_SELECTED_STUDY"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_STUDY
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleStudyById(id))
  } else {
    return dispatch(returnSingleStudyPromise(id)); // return promise that contains study
  }
}

export const returnSingleStudyPromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_STUDY_WITHOUT_FETCHING"
      , id: id
      , item: getState().study.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_STUDY = "REQUEST_SINGLE_STUDY";
function requestSingleStudy(id) {
  return {
    type: REQUEST_SINGLE_STUDY
    , id
  }
}

export const RECEIVE_SINGLE_STUDY = "RECEIVE_SINGLE_STUDY";
function receiveSingleStudy(json) {
  return {
    type: RECEIVE_SINGLE_STUDY
    , id: json.group ? json.group._id : null
    , item: json.group
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleStudyById(studyId) {
  return dispatch => {
    dispatch(requestSingleStudy(studyId))
    return callAPI(`/api/groups/${studyId}`)
      .then(json => dispatch(receiveSingleStudy(json)))
  }
}

export const ADD_SINGLE_STUDY_TO_MAP = "ADD_SINGLE_STUDY_TO_MAP";
export function addSingleStudyToMap(item) {
  return {
    type: ADD_SINGLE_STUDY_TO_MAP
    , item
  }
}

export const REQUEST_SINGLE_STUDY_BY_SLUG = "REQUEST_SINGLE_STUDY_BY_SLUG";
function requestSingleStudyBySlug(slug) {
  return {
    type: REQUEST_SINGLE_STUDY_BY_SLUG
    , slug
  }
}

export function fetchSingleBySlug(studySlug) {
  return dispatch => {
    dispatch(requestSingleStudyBySlug(studySlug))
    return callAPI(`/api/groups/by-slug/${studySlug}`)
      .then(json => dispatch(receiveSingleStudy(json)))
  }
}


export const REQUEST_CREATE_STUDY = "REQUEST_CREATE_STUDY";
function requestCreateStudy(study) {
  return {
    type: REQUEST_CREATE_STUDY
    , study
  }
}

export const RECEIVE_CREATE_STUDY = "RECEIVE_CREATE_STUDY";
function receiveCreateStudy(json) {
  return {
    type: RECEIVE_CREATE_STUDY
    , id: json.group ? json.group._id : null
    , item: json.group
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateStudy(data) {
  return dispatch => {
    dispatch(requestCreateStudy(data))
    return callAPI('/api/groups', 'POST', data)
      .then(json => dispatch(receiveCreateStudy(json)))
  }
}

export const REQUEST_UPDATE_STUDY = "REQUEST_UPDATE_STUDY";
function requestUpdateStudy(study) {
  return {
    type: REQUEST_UPDATE_STUDY
    , id: study._id
    , study
  }
}

export const RECEIVE_UPDATE_STUDY = "RECEIVE_UPDATE_STUDY";
function receiveUpdateStudy(json) {
  return {
    type: RECEIVE_UPDATE_STUDY
    , id: json.group ? json.group._id : null
    , item: json.group
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateStudy(data) {
  return dispatch => {
    dispatch(requestUpdateStudy(data))
    return callAPI(`/api/groups/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateStudy(json)))
  }
}

export const REQUEST_DELETE_STUDY = "REQUEST_DELETE_STUDY";
function requestDeleteStudy(studyId) {
  return {
    type: REQUEST_DELETE_STUDY
    , studyId
  }
}

export const RECEIVE_DELETE_STUDY = "RECEIVE_DELETE_STUDY";
function receiveDeleteStudy(json) {
  return {
    type: RECEIVE_DELETE_STUDY
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteStudy(id))
    return callAPI(`/api/groups/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteStudy(json)))
  }
}


/**
 * STUDY LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest studyLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.study.lists, {});
  let list = { ...state.study.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnStudyListPromise(...listArgs));
  }
}

export const returnStudyListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.study.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_STUDY_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_STUDY_LIST = "REQUEST_STUDY_LIST"
function requestStudyList(listArgs) {
  return {
    type: REQUEST_STUDY_LIST
    , listArgs
  }
}

export const RECEIVE_STUDY_LIST = "RECEIVE_STUDY_LIST"
function receiveStudyList(json, listArgs) {
  return {
    type: RECEIVE_STUDY_LIST
    , listArgs
    , list: json.groups
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_STUDY_TO_LIST = "ADD_STUDY_TO_LIST";
export function addStudyToList(id, ...listArgs) {
  // console.log("Add study to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_STUDY_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_STUDY_FROM_LIST = "REMOVE_STUDY_FROM_LIST"
export function removeStudyFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_STUDY_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    dispatch(requestStudyList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/groups/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/groups/by-[ARG1]/[ARG2]".
     *    ex: /api/groups/by-category/:category
     *  if 2nd arg is an array, though, return "/api/groups/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/groups";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of studies given an array of study id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveStudyList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_STUDY_FILTER = "SET_STUDY_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_STUDY_FILTER
    , filter
    , listArgs
  }
}

export const SET_STUDY_PAGINATION = "SET_STUDY_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_STUDY_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_STUDY_LIST = "INVALIDATE_STUDY_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_STUDY_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/study/studyActions.js